// src/components/crm/DeletedQuotesTable.js — estdel-f9ac8ba8
//
// The "Deleted (N)" view on the Estimates list: every deleted (hidden) estimate the viewer could
// see before it was deleted. Estimate, Customer, Total, Deleted by, Deleted on, and Restore for an
// Owner / Admin. A row opens the estimate, which shows its "Deleted by …" banner. Nothing is
// erased — Restore puts it straight back (setQuoteArchived, server-checked).
import React, { useMemo, useState } from 'react';
import { RotateCcw, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate, toJsDate } from '../../utils/formatters';
import { canRestoreQuote } from '../../utils/quoteSchema';
import { setQuoteArchived, archiveErrorMessage, QuoteRestoredNotice } from './QuoteDeleteControls';

const deletedAtMs = (q) => { const d = toJsDate(q.archivedAt); return d ? d.getTime() : 0; };

const DeletedQuotesTable = ({ quotes, currentUserProfile, onQuoteSelect, term }) => {
    const [busyId, setBusyId] = useState(null);
    const [errorById, setErrorById] = useState({});
    const [restored, setRestored] = useState(null); // the estimate just restored → green line
    const rows = useMemo(() => [...(quotes || [])].sort((a, b) => deletedAtMs(b) - deletedAtMs(a)), [quotes]);
    const canRestore = (q) => canRestoreQuote(q, currentUserProfile?.role);

    const restore = async (e, q) => {
        e.stopPropagation();
        if (busyId) return;
        setBusyId(q.id);
        setErrorById((m) => ({ ...m, [q.id]: null }));
        try {
            await setQuoteArchived(q.id, false);
            setRestored(q);
        } catch (err) {
            setErrorById((m) => ({ ...m, [q.id]: archiveErrorMessage(err, true) }));
        } finally {
            setBusyId(null);
        }
    };

    const restoreButton = (q) => canRestore(q) && (
        <button type="button" onClick={(e) => restore(e, q)} disabled={!!busyId}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60">
            {busyId === q.id ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RotateCcw size={14} aria-hidden="true" />}
            {busyId === q.id ? 'Restoring…' : 'Restore'}
        </button>
    );
    const errorLine = (q) => errorById[q.id] && <p role="alert" className="mt-1 text-xs text-red-600">{errorById[q.id]}</p>;

    return (
        <div>
            {restored && <QuoteRestoredNotice quote={restored} onDismiss={() => setRestored(null)} />}
            <p className="mb-3 text-sm text-gray-500">
                Deleted {term.wordPlural.toLowerCase()} are hidden from lists, reports and the customer portal, and kept on record. Open one to see it.
            </p>

            {rows.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-gray-500">No deleted {term.wordPlural.toLowerCase()}.</p>
                </div>
            ) : (
                <>
                    {/* desktop */}
                    <div className="hidden md:block bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                                <tr>
                                    <th className="px-6 py-3">{term.word}</th>
                                    <th className="px-6 py-3">Customer</th>
                                    <th className="px-6 py-3 text-right">Total</th>
                                    <th className="px-6 py-3">Deleted by</th>
                                    <th className="px-6 py-3">Deleted on</th>
                                    <th className="px-6 py-3 text-right"><span className="sr-only">Restore</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((q) => (
                                    <tr key={q.id} onClick={() => onQuoteSelect(q)} className="bg-white border-b hover:bg-gray-50 cursor-pointer transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-900">{q.quoteNumber || '—'}</td>
                                        <td className="px-6 py-4">{q.customerName || '—'}</td>
                                        <td className="px-6 py-4 text-right font-semibold text-gray-900">{formatCurrency(q.total)}</td>
                                        <td className="px-6 py-4">{q.archivedBy?.name || '—'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{q.archivedAt ? formatDate(toJsDate(q.archivedAt)) : '—'}</td>
                                        <td className="px-6 py-4 text-right">{restoreButton(q)}{errorLine(q)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* phone */}
                    <div className="md:hidden space-y-3">
                        {rows.map((q) => (
                            <div key={q.id} onClick={() => onQuoteSelect(q)} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer">
                                <div className="flex justify-between items-start gap-3">
                                    <div className="min-w-0">
                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">#{q.quoteNumber || '—'}</span>
                                        <h3 className="text-lg font-bold text-gray-900 truncate">{q.customerName || 'Unknown Customer'}</h3>
                                    </div>
                                    <div className="text-lg font-bold text-gray-700 shrink-0">{formatCurrency(q.total)}</div>
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    Deleted{q.archivedBy?.name ? ` by ${q.archivedBy.name}` : ''}{q.archivedAt ? ` on ${formatDate(toJsDate(q.archivedAt))}` : ''}
                                </p>
                                {canRestore(q) && <div className="mt-3">{restoreButton(q)}</div>}
                                {errorLine(q)}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default DeletedQuotesTable;
